/**
 * Charter Contracts — the auctioned scoring objectives.
 *
 * Charters are the game's "tell people what you want" mechanism. They are bought in a
 * simultaneous sealed Data auction on odd rounds, which means a charter you badly want
 * is also a charter your opponents can see you wanting. Each is worth 3–6 VP and is
 * tested once, at final scoring.
 *
 * Design note: every charter is achievable from any board position, and none of them
 * reward doing something a player would do anyway — each one bends play a little.
 */

import { trackLevel } from './tech';
import {
  colonyCount, countOwnedInRegion, countOwnedOfClass, effectiveHabitability, fleetCount,
  holdingCount, systemsOwnedBy,
} from './queries';
import type { Charter, CharterId, GameState, Player } from './types';

export const CHARTERS: Readonly<Record<CharterId, Charter>> = Object.freeze({
  gasBaron: {
    id: 'gasBaron', name: 'Gas Baron', vp: 4,
    blurb: 'Hold 3 or more Gas Giants.',
    test: (s, p) => countOwnedOfClass(s, p.id, 'gasGiant') >= 3,
  },
  greenThumb: {
    id: 'greenThumb', name: 'Green Thumb', vp: 4,
    blurb: 'Hold 2 or more worlds at Habitability 3.',
    test: (s, p) =>
      systemsOwnedBy(s, p.id).filter((sys) => effectiveHabitability(s, sys) >= 3).length >= 2,
  },
  deepCore: {
    id: 'deepCore', name: 'Deep Core Writ', vp: 5,
    blurb: 'Hold 2 or more systems in The Core.',
    test: (s, p) => countOwnedInRegion(s, p.id, 'core') >= 2,
  },
  cartographer: {
    id: 'cartographer', name: 'Cartographer', vp: 3,
    blurb: 'Personally survey 6 or more systems.',
    test: (_s, p) => p.stats.systemsSurveyed >= 6,
  },
  ironWorks: {
    id: 'ironWorks', name: 'Iron Works', vp: 4,
    blurb: 'Hold 4 or more Ore-producing systems (Volcanic, Desert or Asteroid).',
    test: (s, p) =>
      countOwnedOfClass(s, p.id, 'volcanic') +
      countOwnedOfClass(s, p.id, 'desert') +
      countOwnedOfClass(s, p.id, 'asteroid') >= 4,
  },
  archivist: {
    id: 'archivist', name: 'Archivist', vp: 4,
    blurb: 'Hold 5 or more techs.',
    test: (_s, p) => p.techs.length >= 5,
  },
  pathfinder: {
    id: 'pathfinder', name: 'Pathfinder', vp: 3,
    blurb: 'Reach Drive tier III.',
    test: (_s, p) => trackLevel(p, 'drive') >= 3,
  },
  terraformer: {
    id: 'terraformer', name: 'Terraformer', vp: 5,
    blurb: 'Terraform 3 or more times.',
    test: (_s, p) => p.stats.terraformed >= 3,
  },
  admiral: {
    id: 'admiral', name: 'Admiral', vp: 4,
    blurb: 'Win 2 or more Contests and finish with 3 or more fleets.',
    test: (s, p) => p.stats.contestsWon >= 2 && fleetCount(s, p.id) >= 3,
  },
  settler: {
    id: 'settler', name: 'Settler', vp: 5,
    blurb: 'Hold 4 or more Colonies.',
    test: (s, p) => colonyCount(s, p.id) >= 4,
  },
  relicHunter: {
    id: 'relicHunter', name: 'Relic Hunter', vp: 4,
    blurb: 'Recover 2 or more Relics.',
    test: (_s, p) => p.relics.length >= 2,
  },
  population: {
    id: 'population', name: 'Great Migration', vp: 4,
    blurb: 'Finish with 8 or more Population and 6 or more holdings.',
    test: (s, p) => p.population >= 8 && holdingCount(s, p.id) >= 6,
  },
});

export const ALL_CHARTER_IDS: readonly CharterId[] = Object.keys(CHARTERS) as CharterId[];

export function charter(id: CharterId): Charter {
  return CHARTERS[id];
}

/** Whether a charter the player holds is currently satisfied. */
export function charterMet(state: GameState, player: Player, id: CharterId): boolean {
  return CHARTERS[id].test(state, player);
}

/** VP the player would bank from their charters right now. */
export function charterVp(state: GameState, player: Player): number {
  let total = 0;
  for (const id of player.charters) {
    if (CHARTERS[id].test(state, player)) total += CHARTERS[id].vp;
  }
  return total;
}

/**
 * Progress hint for the HUD: `[done, needed]`. Kept deliberately coarse — the exact
 * predicate lives in `test`, this is only for drawing a little progress pip row.
 */
export function charterProgress(
  state: GameState,
  player: Player,
  id: CharterId,
): [number, number] {
  switch (id) {
    case 'gasBaron': return [countOwnedOfClass(state, player.id, 'gasGiant'), 3];
    case 'greenThumb': return [
      systemsOwnedBy(state, player.id).filter((s) => effectiveHabitability(state, s) >= 3).length,
      2,
    ];
    case 'deepCore': return [countOwnedInRegion(state, player.id, 'core'), 2];
    case 'cartographer': return [player.stats.systemsSurveyed, 6];
    case 'ironWorks': return [
      countOwnedOfClass(state, player.id, 'volcanic') +
      countOwnedOfClass(state, player.id, 'desert') +
      countOwnedOfClass(state, player.id, 'asteroid'),
      4,
    ];
    case 'archivist': return [player.techs.length, 5];
    case 'pathfinder': return [trackLevel(player, 'drive'), 3];
    case 'terraformer': return [player.stats.terraformed, 3];
    case 'admiral': return [player.stats.contestsWon, 2];
    case 'settler': return [colonyCount(state, player.id), 4];
    case 'relicHunter': return [player.relics.length, 2];
    case 'population': return [player.population, 8];
  }
}
